/**
 * Smistamento: solo pannello amministratore (isDefaultUser).
 * Il menu mostra la voce solo agli admin; qui si evita accesso diretto a /smistamento.
 */
import React, { useState, useEffect, useContext, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Table,
  Alert,
  Spinner,
  Card,
  Button,
  Form,
  Badge,
  InputGroup,
} from 'react-bootstrap';
import Navbar from '../components/Navbar';
import { ThemeContext } from '../context/ThemeContext';
import { DestinazioneContext } from '../context/DestinazioneContext';
import { useAlert } from '../context/AlertContext';
import configService from '../services/configService';
import SmistamentoPrintView from '../components/SmistamentoPrintView';
import {
  destinazioneHeadline,
  luogoZonaValue,
  indirizzoGpsValue,
  telefonoDestValue,
  mapsSearchUrlFromAddress,
  telefonoDialHref,
  sortDestinazioniByLuogoZona,
  filterDestinazioniByLuogoZona,
  smistamentoEquivalentePlanning,
  smistamentoFormatQuantitaCell,
} from '../utils/smistamentoHelpers';
import useIsMobile from '../hooks/useIsMobile';

function getTodayIsoItaly() {
  const d = new Date();
  const it = new Date(d.toLocaleString('en-US', { timeZone: 'Europe/Rome' }));
  const y = it.getFullYear();
  const m = String(it.getMonth() + 1).padStart(2, '0');
  const day = String(it.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Larghezze colonne tabella righe: 6/12 · 1/12 · 2/12 · 3/12 (Descrizione = metà tabella). */
const SMISTAMENTO_RIGHE_CELL_STYLE = {
  wordBreak: 'break-word',
  overflowWrap: 'anywhere',
};

function extractSmistamentoRows(response) {
  if (!response) return [];
  if (Array.isArray(response)) return response;
  if (Array.isArray(response.data)) return response.data;
  if (response.data && Array.isArray(response.data.data)) return response.data.data;
  return [];
}

const Smistamento = () => {
  const navigate = useNavigate();
  const { darkMode } = useContext(ThemeContext);
  const { destinazione } = useContext(DestinazioneContext);
  const { showAlertGlobal } = useAlert();

  useEffect(() => {
    if (localStorage.getItem('isDefaultUser') !== 'true') {
      navigate('/home', { replace: true });
    }
  }, [navigate]);

  /** Giorno lista/ricerca: default oggi (Europe/Rome). */
  const [giornoEsatto, setGiornoEsatto] = useState(() => getTodayIsoItaly());
  /** API: descrizione o codice articolo (un solo campo). */
  const [filterDescrizione, setFilterDescrizione] = useState('');
  /** API: nome o codice cliente (ricerca incrementale). */
  const [filterClienteDest, setFilterClienteDest] = useState('');
  /** Filtro su `luogo_zona` (anche inviato all’API su «Aggiorna»; applicato subito in UI). */
  const [filterLuogoZona, setFilterLuogoZona] = useState('');
  /** Ordina le destinazioni per `luogo_zona` e mostra il codice zona prima di ogni blocco. */
  const [ordinaPerLuogoZona, setOrdinaPerLuogoZona] = useState(false);

  const [destinazioni, setDestinazioni] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savingKey, setSavingKey] = useState(null);
  const isMobile = useIsMobile();
  const clienteDebounceTimer = useRef(null);
  const skipClienteDebounceOnce = useRef(true);

  const buildRequestBody = useCallback(() => {
    const body = { giorno_esatto: giornoEsatto };
    if (isMobile) {
      return body;
    }
    if (String(filterDescrizione).trim() !== '') {
      body.filterDescrizione = String(filterDescrizione).trim();
    }
    if (String(filterClienteDest).trim() !== '') {
      body.filterClienteDest = String(filterClienteDest).trim();
    }
    if (String(filterLuogoZona).trim() !== '') {
      body.filterLuogoZona = String(filterLuogoZona).trim();
    }
    return body;
  }, [giornoEsatto, filterDescrizione, filterClienteDest, filterLuogoZona, isMobile]);

  const loadSmistamento = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await configService.getSmistamento(buildRequestBody());
      setDestinazioni(extractSmistamentoRows(res));
    } catch (e) {
      const msg = e?.response?.data?.message || e.message;
      setError(String(msg));
      showAlertGlobal('Errore caricamento smistamento', 'danger');
      setDestinazioni([]);
    } finally {
      setLoading(false);
    }
  }, [buildRequestBody, showAlertGlobal]);

  useEffect(() => {
    if (localStorage.getItem('isDefaultUser') !== 'true') return;
    if (destinazione) loadSmistamento();
  }, [destinazione, giornoEsatto, loadSmistamento]);

  useEffect(() => {
    skipClienteDebounceOnce.current = true;
  }, [destinazione]);

  /** Ricerca incrementale solo su «Filtra per cliente». */
  useEffect(() => {
    if (localStorage.getItem('isDefaultUser') !== 'true' || !destinazione) return;
    if (isMobile) return;
    if (skipClienteDebounceOnce.current) {
      skipClienteDebounceOnce.current = false;
      return;
    }
    if (clienteDebounceTimer.current) clearTimeout(clienteDebounceTimer.current);
    clienteDebounceTimer.current = setTimeout(() => {
      clienteDebounceTimer.current = null;
      loadSmistamento();
    }, 450);
    return () => {
      if (clienteDebounceTimer.current) clearTimeout(clienteDebounceTimer.current);
    };
  }, [filterClienteDest, destinazione, loadSmistamento, isMobile]);

  const destKey = (d) => `${d.cod_clie ?? d.codiceCliente ?? ''}_${d.cod_dest ?? d.codiceDestinazione ?? ''}`;

  const destinazioniFiltratePerZona = useMemo(
    () => filterDestinazioniByLuogoZona(destinazioni, filterLuogoZona),
    [destinazioni, filterLuogoZona]
  );

  const destinazioniVisualizzate = useMemo(() => {
    let rows = destinazioniFiltratePerZona;
    if (isMobile && String(filterClienteDest).trim() !== '') {
      const q = String(filterClienteDest).trim().toLowerCase();
      rows = rows.filter((d) => {
        const headline = String(destinazioneHeadline(d) || '').toLowerCase();
        const zona = String(luogoZonaValue(d) || '').toLowerCase();
        const righe = Array.isArray(d.righe) ? d.righe : [];
        const inRighe = righe.some((r) => {
          const descriz = String(r.descriz || r.descrizione || '').toLowerCase();
          const codice = String(r.codice || r.cd_min || r.codice_prod || '').toLowerCase();
          return descriz.includes(q) || codice.includes(q);
        });
        return headline.includes(q) || zona.includes(q) || inRighe;
      });
    }
    if (!ordinaPerLuogoZona) return rows;
    return sortDestinazioniByLuogoZona(rows);
  }, [destinazioniFiltratePerZona, ordinaPerLuogoZona, isMobile, filterClienteDest]);

  const titoloPanificioStampa = useMemo(() => {
    if (!destinazione) return '';
    return String(
      destinazione.nomede || destinazione.nome || destinazione.ragione_sociale || ''
    ).trim();
  }, [destinazione]);

  const hasActiveFilters = useMemo(
    () =>
      String(filterLuogoZona).trim() !== '' ||
      String(filterClienteDest).trim() !== '' ||
      String(filterDescrizione).trim() !== '' ||
      ordinaPerLuogoZona,
    [filterLuogoZona, filterClienteDest, filterDescrizione, ordinaPerLuogoZona]
  );

  const toggleCompleted = async (d, completed) => {
    const cod_clie = d.cod_clie ?? d.codiceCliente;
    const cod_dest = d.cod_dest ?? d.codiceDestinazione;
    const key = destKey(d);
    try {
      setSavingKey(key);
      await configService.postSmistamentoCompleted({
        giorno_esatto: giornoEsatto,
        cod_clie: String(cod_clie),
        cod_dest: String(cod_dest),
        completed,
      });
      setDestinazioni((prev) =>
        prev.map((row) =>
          destKey(row) === key
            ? { ...row, completed, completed_at: completed ? new Date().toISOString() : null }
            : row
        )
      );
      showAlertGlobal(completed ? 'Segnato completato' : 'Completamento rimosso', 'success');
    } catch (e) {
      const msg = e?.response?.data?.message || e.message;
      showAlertGlobal(String(msg), 'danger');
    } finally {
      setSavingKey(null);
    }
  };

  const filterCard = (
    <Card className="mb-4">
      <Card.Header className="card-header-sidebar-bg">
        <h5 className="mb-0">
          <i className="fas fa-filter me-2" />
          Filtri
        </h5>
      </Card.Header>
      <Card.Body className={darkMode ? 'text-light' : ''}>
        <Row className="g-2 g-md-2 align-items-end">
          <Col xs={6} md={2} lg={2}>
            <Form.Group className="mb-0">
              <Form.Label className="small mb-1">Giorno</Form.Label>
              <Form.Control
                type="date"
                size={isMobile ? undefined : 'sm'}
                value={giornoEsatto}
                onChange={(e) => setGiornoEsatto(e.target.value)}
                className={darkMode ? 'bg-dark text-light border-secondary' : ''}
              />
            </Form.Group>
          </Col>
          <Col xs={6} md={2} lg={2}>
            <Form.Group className="mb-0">
              <Form.Label className="small mb-1">Zona</Form.Label>
              <Form.Control
                size={isMobile ? undefined : 'sm'}
                value={filterLuogoZona}
                onChange={(e) => setFilterLuogoZona(e.target.value)}
                placeholder="luogo_zona"
                className={darkMode ? 'bg-dark text-light border-secondary' : ''}
                title="Filtro immediato; Aggiorna invia filterLuogoZona anche al server"
              />
            </Form.Group>
          </Col>
          <Col xs={12} md={4} lg={4}>
            <Form.Group className="mb-0">
              <Form.Label className="small mb-1">Cliente o nome destinazione</Form.Label>
              <Form.Control
                size={isMobile ? undefined : 'sm'}
                value={filterClienteDest}
                onChange={(e) => setFilterClienteDest(e.target.value)}
                placeholder="Nome/codice cliente o destinazione"
                className={darkMode ? 'bg-dark text-light border-secondary' : ''}
              />
            </Form.Group>
          </Col>
          <Col xs={12} md={4} lg={4}>
            <Form.Group className="mb-0">
              <Form.Label className="small mb-1">Articolo</Form.Label>
              <Form.Control
                size={isMobile ? undefined : 'sm'}
                value={filterDescrizione}
                onChange={(e) => setFilterDescrizione(e.target.value)}
                placeholder="Descrizione o codice articolo"
                className={darkMode ? 'bg-dark text-light border-secondary' : ''}
              />
            </Form.Group>
          </Col>
        </Row>
        <div className="mt-3">
          <div className="d-flex align-items-center justify-content-between gap-3 mb-2">
            <Form.Check
              type="switch"
              id="smistamento-ordina-luogo-zona"
              label="Ordina per luogo zona"
              checked={ordinaPerLuogoZona}
              onChange={(e) => setOrdinaPerLuogoZona(e.target.checked)}
              className={`${darkMode ? 'text-light' : ''} mb-0 flex-grow-1`}
            />
            {hasActiveFilters && (
              <small className="text-muted text-nowrap mb-0">
                <i className="fas fa-filter me-1"></i>
                Filtri attivi
              </small>
            )}
          </div>
          <div className="d-flex align-items-center justify-content-between gap-3">
            <Button variant="primary" size={isMobile ? undefined : 'sm'} onClick={loadSmistamento} disabled={loading} className="mb-0">
              {loading ? (
                <>
                  <Spinner size="sm" className="me-2" />
                  Caricamento…
                </>
              ) : (
                <>
                  <i className="fas fa-sync-alt me-2" />
                  Aggiorna
                </>
              )}
            </Button>
            {hasActiveFilters && (
              <div className="d-flex flex-wrap justify-content-end gap-1">
                {String(filterLuogoZona).trim() !== '' && <Badge bg="info">Zona</Badge>}
                {String(filterClienteDest).trim() !== '' && <Badge bg="primary">Cliente</Badge>}
                {String(filterDescrizione).trim() !== '' && <Badge bg="secondary">Articolo</Badge>}
                {ordinaPerLuogoZona && (
                  <Badge bg="warning" text="dark">
                    Ordine zona
                  </Badge>
                )}
              </div>
            )}
          </div>
        </div>
      </Card.Body>
    </Card>
  );

  const renderRigheTable = (righe) => {
    const list = Array.isArray(righe) ? righe : [];
    return (
      <div className="table-responsive">
        <Table
          striped
          bordered
          hover
          size="sm"
          className={darkMode ? 'table-dark' : ''}
          style={{ tableLayout: 'fixed', width: '100%' }}
        >
          <colgroup>
            <col style={{ width: '50%' }} />
            <col style={{ width: `${100 / 12}%` }} />
            <col style={{ width: `${(100 / 12) * 2}%` }} />
            <col style={{ width: '25%' }} />
          </colgroup>
          <thead>
            <tr>
              <th style={SMISTAMENTO_RIGHE_CELL_STYLE}>Descrizione</th>
              <th style={SMISTAMENTO_RIGHE_CELL_STYLE}>U.M.</th>
              <th style={SMISTAMENTO_RIGHE_CELL_STYLE}>Quantità</th>
              <th style={SMISTAMENTO_RIGHE_CELL_STYLE}>Corrispondente</th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 ? (
              <tr>
                <td colSpan={4} className="text-muted text-center">
                  Nessuna riga
                </td>
              </tr>
            ) : (
              list.map((r, idx) => (
                <tr key={idx}>
                  <td style={SMISTAMENTO_RIGHE_CELL_STYLE}>{r.descriz || r.descrizione || '—'}</td>
                  <td style={SMISTAMENTO_RIGHE_CELL_STYLE}>{r.unita_m || r.unita_di_misura || '—'}</td>
                  <td style={SMISTAMENTO_RIGHE_CELL_STYLE}>
                    {smistamentoFormatQuantitaCell(r.quantita ?? r.quantita_totale)}
                  </td>
                  <td style={SMISTAMENTO_RIGHE_CELL_STYLE}>{smistamentoEquivalentePlanning(r)}</td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>
    );
  };

  const renderDestCard = (d) => {
    const headline = destinazioneHeadline(d);
    const indirizzo = indirizzoGpsValue(d);
    const telefono = telefonoDestValue(d);
    const mapsUrl = indirizzo ? mapsSearchUrlFromAddress(indirizzo) : '';
    const telHref = telefono ? telefonoDialHref(telefono) : '';
    const linkClass = darkMode ? 'link-light text-break' : 'link-primary text-break';
    const done =
      d.completed === true ||
      d.completed === 1 ||
      d.completed === '1' ||
      d.completed === 'true';
    const key = destKey(d);
    const busy = savingKey === key;

    return (
      <Card key={key} className={`mb-3 ${darkMode ? 'bg-dark text-light border-secondary' : ''}`}>
        <Card.Header className="d-flex flex-wrap align-items-start justify-content-between gap-2 card-header-sidebar-bg">
          <div className="flex-grow-1" style={{ minWidth: 0 }}>
            <strong>{headline}</strong>
            {indirizzo && mapsUrl ? (
              <div className="small mt-1">
                <i className="fas fa-location-arrow me-1" aria-hidden="true" />
                <a
                  href={mapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={linkClass}
                  onClick={(e) => e.stopPropagation()}
                >
                  {indirizzo}
                </a>
              </div>
            ) : null}
            {telefono && telHref ? (
              <div className="small mt-1">
                <i className="fas fa-phone me-1" aria-hidden="true" />
                <a href={telHref} className={linkClass} onClick={(e) => e.stopPropagation()}>
                  {telefono}
                </a>
              </div>
            ) : null}
          </div>
          <Button
            variant={done ? 'secondary' : 'success'}
            size="sm"
            disabled={busy}
            onClick={() => toggleCompleted(d, !done)}
          >
            {busy ? <Spinner size="sm" animation="border" /> : null}{' '}
            {done ? 'Completato' : 'Completa'}
          </Button>
        </Card.Header>
        <Card.Body>{renderRigheTable(d.righe)}</Card.Body>
      </Card>
    );
  };

  const renderDestinazioniConEventualiZona = () => {
    const list = destinazioniVisualizzate;
    if (!ordinaPerLuogoZona) {
      return list.map(renderDestCard);
    }
    const out = [];
    let prevZonaNorm = null;
    list.forEach((d) => {
      const zona = luogoZonaValue(d);
      const zonaNorm = zona || '__senza_zona__';
      if (zonaNorm !== prevZonaNorm) {
        prevZonaNorm = zonaNorm;
        const label = zona || 'Senza zona';
        out.push(
          <div
            key={`zona-sep-${zonaNorm}-${destKey(d)}`}
            className={`mb-2 px-2 py-1 rounded small fw-semibold ${
              darkMode ? 'bg-secondary text-white' : 'bg-light border'
            }`}
          >
            <i className="fas fa-map-marker-alt me-2" aria-hidden="true" />
            Codice zona: {label}
          </div>
        );
      }
      out.push(renderDestCard(d));
    });
    return out;
  };

  const handleStampa = () => {
    if (destinazioniFiltratePerZona.length === 0) return;
    window.print();
  };

  const renderMainContent = () => {
    if (loading) {
      return (
        <div className="text-center py-5">
          <Spinner animation="border" />
          <p className="mt-2">Caricamento…</p>
        </div>
      );
    }
    if (error) {
      return <Alert variant="danger">{error}</Alert>;
    }
    if (destinazioni.length === 0) {
      return <Alert variant="info">Nessuna destinazione per questo giorno.</Alert>;
    }
    if (destinazioniFiltratePerZona.length === 0) {
      return (
        <Alert variant="warning">
          Nessuna destinazione corrisponde al filtro zona. Modifica o svuota «Filtra per codice zona».
        </Alert>
      );
    }
    return renderDestinazioniConEventualiZona();
  };

  const shellBg = darkMode ? '#121212' : '#FFFEF9';
  const stickyStyle = {
    backgroundColor: shellBg,
    borderBottom: `1px solid ${darkMode ? '#495057' : '#dee2e6'}`,
    boxShadow: darkMode ? '0 2px 8px rgba(0,0,0,0.35)' : '0 2px 8px rgba(0,0,0,0.06)',
  };

  return (
    <>
      <div className={`app ${darkMode ? 'dark-mode' : ''} d-print-none`}>
        <Navbar />
        {!isMobile ? (
          <Container fluid className="mt-5 pt-3">
            <Row className="justify-content-center">
              <Col xs={12}>
                <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                  <h2 className={darkMode ? 'text-light' : 'text-dark'}>
                    <i className="fas fa-random me-2" />
                    Smistamento
                  </h2>
                  <Button
                    variant="outline-secondary"
                    className={darkMode ? 'border-secondary text-light' : ''}
                    disabled={loading || destinazioniFiltratePerZona.length === 0}
                    onClick={handleStampa}
                  >
                    <i className="fas fa-print me-2" aria-hidden="true" />
                    Stampa elenco
                  </Button>
                </div>

                {filterCard}

                {renderMainContent()}
              </Col>
            </Row>
          </Container>
        ) : (
          <div className="smistamento-mobile-shell" style={{ backgroundColor: shellBg }}>
            <div className="smistamento-mobile-sticky px-3 pt-2 pb-2" style={stickyStyle}>
              <div
                className={`d-flex justify-content-between align-items-center mb-2 ${darkMode ? 'text-light' : 'text-dark'}`}
              >
                <h4 className="mb-0">
                  <i className="fas fa-random me-2" />
                  Smistamento
                </h4>
                <div className="d-flex align-items-center gap-1 flex-shrink-0 ms-2">
                  <Button
                    variant="outline-secondary"
                    className={darkMode ? 'border-secondary text-light' : ''}
                    disabled={loading || destinazioniFiltratePerZona.length === 0}
                    onClick={handleStampa}
                    aria-label="Stampa elenco"
                  >
                    <i className="fas fa-print" aria-hidden="true" />
                  </Button>
                  <Button
                    variant="primary"
                    onClick={loadSmistamento}
                    disabled={loading}
                    className="d-flex align-items-center"
                  >
                    {loading ? (
                      <Spinner size="sm" animation="border" />
                    ) : (
                      <>
                        <i className="fas fa-sync-alt me-1" />
                        Aggiorna
                      </>
                    )}
                  </Button>
                </div>
              </div>

              <Form.Control
                type="date"
                value={giornoEsatto}
                onChange={(e) => setGiornoEsatto(e.target.value)}
                className={`mb-2 ${darkMode ? 'bg-dark text-light border-secondary' : ''}`}
                aria-label="Giorno"
              />

              <InputGroup className="mb-2">
                <InputGroup.Text className={darkMode ? 'bg-dark text-light border-secondary' : ''}>
                  <i className="fas fa-search" />
                </InputGroup.Text>
                <Form.Control
                  value={filterClienteDest}
                  onChange={(e) => setFilterClienteDest(e.target.value)}
                  placeholder="Cerca cliente, destinazione o zona..."
                  className={darkMode ? 'bg-dark text-light border-secondary' : ''}
                />
                {filterClienteDest && (
                  <Button
                    variant="outline-secondary"
                    onClick={() => setFilterClienteDest('')}
                    className={darkMode ? 'border-secondary text-light' : ''}
                  >
                    <i className="fas fa-times" />
                  </Button>
                )}
              </InputGroup>

              <div className="mt-1">
                <div className="d-flex align-items-center justify-content-between gap-2 mb-1">
                  <Form.Check
                    type="switch"
                    id="mobile-smistamento-ordina-luogo-zona"
                    label="Ordina per luogo zona"
                    checked={ordinaPerLuogoZona}
                    onChange={(e) => setOrdinaPerLuogoZona(e.target.checked)}
                    className={`${darkMode ? 'text-light' : ''} mb-0 flex-grow-1`}
                    style={{ minWidth: 0 }}
                  />
                  {hasActiveFilters && (
                    <small className="text-muted text-nowrap mb-0">
                      <i className="fas fa-filter me-1" />
                      Filtri attivi
                    </small>
                  )}
                </div>
                {hasActiveFilters && (
                  <div className="d-flex justify-content-end gap-1 flex-wrap">
                    {String(filterLuogoZona).trim() !== '' && <Badge bg="info">Zona</Badge>}
                    {String(filterClienteDest).trim() !== '' && <Badge bg="primary">Cliente</Badge>}
                    {String(filterDescrizione).trim() !== '' && <Badge bg="secondary">Articolo</Badge>}
                    {ordinaPerLuogoZona && (
                      <Badge bg="warning" text="dark">
                        Ordine zona
                      </Badge>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="smistamento-mobile-scroll px-3 py-2">{renderMainContent()}</div>
          </div>
        )}
      </div>

      <SmistamentoPrintView
        destinazioni={destinazioniVisualizzate}
        ordinaPerLuogoZona={ordinaPerLuogoZona}
        giornoEsatto={giornoEsatto}
        filterDescrizione={filterDescrizione}
        filterClienteDest={filterClienteDest}
        filterLuogoZona={filterLuogoZona}
        titoloPanificio={titoloPanificioStampa}
      />
    </>
  );
};

export default Smistamento;
